import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { signOffWorkOrder } from '../services/api';
import { toast } from 'sonner';
import { CheckCircle2, Eraser, PenTool, ShieldCheck } from 'lucide-react';

/**
 * Compact canvas-based signature pad. Records the stroke path and
 * exposes:
 *   - `getDataUrl()`  → PNG data URL (used by the parent modal on save)
 *   - `clear()`       → wipes the canvas
 *   - `isEmpty()`     → true when nothing has been drawn yet
 *
 * Deliberately not a heavyweight library (react-signature-canvas) —
 * the pad is ~40 lines of vanilla canvas + touch/pointer events, which
 * is trivial to inspect, has zero deps, and renders the exact same
 * signature at any DPR because we scale for device pixel ratio.
 */
const SignaturePad = React.forwardRef(function SignaturePad({ height = 200, disabled = false }, ref) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const dirtyRef = useRef(false);

  const paintBackground = useCallback((ctx, w, h) => {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    // Subtle guide line so signers know where to sign.
    ctx.strokeStyle = '#e2e8f0';
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(20, h - 32);
    ctx.lineTo(w - 20, h - 32);
    ctx.stroke();
    ctx.setLineDash([]);
  }, []);

  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2.4;
    ctx.strokeStyle = '#0f172a';
    paintBackground(ctx, rect.width, rect.height);
  }, [paintBackground]);

  useEffect(() => {
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [resize]);

  const localPoint = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return { x: point.clientX - rect.left, y: point.clientY - rect.top };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = localPoint(e);
  };

  const move = (e) => {
    if (!drawingRef.current || disabled) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = localPoint(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    dirtyRef.current = true;
  };

  const end = () => { drawingRef.current = false; };

  React.useImperativeHandle(ref, () => ({
    clear: () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      const rect = canvas.getBoundingClientRect();
      paintBackground(ctx, rect.width, rect.height);
      dirtyRef.current = false;
    },
    getDataUrl: () => canvasRef.current?.toDataURL('image/png'),
    isEmpty: () => !dirtyRef.current,
  }));

  return (
    <div
      className={`relative border border-slate-300 rounded-md bg-white overflow-hidden ${disabled ? 'opacity-50' : ''}`}
      style={{ touchAction: 'none' }}
      data-testid="signature-pad"
    >
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: `${height}px`, display: 'block', cursor: disabled ? 'not-allowed' : 'crosshair' }}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      <div className="pointer-events-none absolute bottom-2 left-4 right-4 text-[10px] uppercase tracking-widest text-slate-400 flex items-center justify-between">
        <span>Sign above the dashed line</span>
        <span><PenTool className="h-3 w-3 inline" /> Finger / mouse / stylus</span>
      </div>
    </div>
  );
});

/**
 * Modal that wraps the SignaturePad and posts the captured PNG to the
 * WO sign-off endpoint. Reusable — same component works for admin,
 * employee, and (if we ever extend it) customer paths.
 */
export const SignOffModal = ({ workOrder, open, onOpenChange, onSigned }) => {
  const padRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!padRef.current || padRef.current.isEmpty()) {
      return toast.error('Please sign above the dashed line before saving.');
    }
    setBusy(true);
    try {
      const dataUrl = padRef.current.getDataUrl();
      const updated = await signOffWorkOrder(workOrder.id, dataUrl);
      toast.success('Sign-off captured — it will appear on every WO PDF from now on.');
      onSigned && onSigned(updated);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save signature');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="sign-off-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" /> Sign off {workOrder?.id}
          </DialogTitle>
          <DialogDescription>
            Your signature will be embedded on the work-order PDF and shown to the customer. This creates
            an audit record — please make sure the work is genuinely complete before signing.
          </DialogDescription>
        </DialogHeader>
        <SignaturePad ref={padRef} height={200} disabled={busy} />
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => padRef.current?.clear()}
            disabled={busy}
            data-testid="signature-clear-btn"
          >
            <Eraser className="h-4 w-4 mr-1.5" /> Clear
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="signature-save-btn"
          >
            <CheckCircle2 className="h-4 w-4 mr-1.5" />
            {busy ? 'Saving…' : 'Save signature'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SignaturePad;
